import { Head } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import type { SeoMeta } from "@/types"

const EFFECTIVE_DATE = "13 February 2026"
const COMPANY_NAME = "QuiteRight"
const COMPANY_ENTITY = "QuiteRight Ltd"
const CONTACT_EMAIL = "team@quiteright.co.uk"
const WEBSITE = "quiteright.co.uk"

export default function Terms({ meta }: { meta: SeoMeta }) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta name="robots" content="noindex, follow" />

        {/* Open Graph */}
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={meta.image} />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.image} />
      </Head>

      <article className="px-6 py-16 md:py-24">
        <div className="mx-auto max-w-3xl">

          {/* Header */}
          <header className="mb-16 text-center">
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">Legal</p>
            <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal md:text-5xl">
              Terms &amp; Conditions<span className="text-red">.</span>
            </h1>
            <p className="mt-4 text-sm text-warm-grey">
              Effective date: {EFFECTIVE_DATE}
            </p>
          </header>

          {/* Body */}
          <div className="prose-brand space-y-10 text-sm leading-relaxed text-slate [&_h2]:mt-12 [&_h2]:mb-4 [&_h2]:font-display [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-charcoal [&_h3]:mt-8 [&_h3]:mb-3 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-charcoal [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:space-y-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1.5 [&_strong]:font-semibold [&_strong]:text-charcoal [&_a]:text-red [&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:text-deep-red">

            {/* 1 */}
            <section>
              <h2>1. Introduction and Acceptance</h2>
              <p>
                These Terms and Conditions (&ldquo;<strong>Terms</strong>&rdquo;) constitute a legally binding agreement between you (&ldquo;<strong>you</strong>&rdquo;, &ldquo;<strong>your</strong>&rdquo;, or &ldquo;<strong>User</strong>&rdquo;) and {COMPANY_ENTITY} (&ldquo;<strong>we</strong>&rdquo;, &ldquo;<strong>us</strong>&rdquo;, &ldquo;<strong>our</strong>&rdquo;, or &ldquo;<strong>{COMPANY_NAME}</strong>&rdquo;), a company registered in England and Wales, governing your access to and use of the {COMPANY_NAME} platform, website ({WEBSITE}), and all related services (collectively, the &ldquo;<strong>Service</strong>&rdquo;).
              </p>
              <p>
                By creating an account, accessing, or using the Service, you confirm that you have read, understood, and agree to be bound by these Terms and our <a href="/privacy">Privacy Policy</a>. If you do not agree, you must not use the Service.
              </p>
              <p>
                If you are entering into these Terms on behalf of a company, organisation, or other legal entity, you represent and warrant that you have the authority to bind that entity to these Terms, in which case &ldquo;you&rdquo; and &ldquo;your&rdquo; shall refer to that entity.
              </p>
            </section>

            {/* 2 */}
            <section>
              <h2>2. Definitions</h2>
              <p>In these Terms, unless the context otherwise requires:</p>
              <ul>
                <li><strong>&ldquo;Account&rdquo;</strong> means the tenant account created upon registration, to which Users, Listings, and other resources are associated.</li>
                <li><strong>&ldquo;Content&rdquo;</strong> means any text, images, video, audio, data, or other material uploaded to, created by, or generated through the Service.</li>
                <li><strong>&ldquo;Generated Content&rdquo;</strong> means video advertisements, scripts, captions, voiceover audio, and any other output produced by the Service using automated rendering or programmatic generation.</li>
                <li><strong>&ldquo;Listing&rdquo;</strong> means a property listing imported into or created within the Service.</li>
                <li><strong>&ldquo;Listing Data&rdquo;</strong> means property information including but not limited to addresses, descriptions, prices, photographs, and features, whether imported from third-party sources or manually entered.</li>
                <li><strong>&ldquo;Subscription&rdquo;</strong> means any paid plan providing access to the Service.</li>
                <li><strong>&ldquo;Third-Party Platforms&rdquo;</strong> means social media services including but not limited to Instagram, TikTok, YouTube, Facebook, and LinkedIn, and property portal services including but not limited to Rightmove, Zoopla, OnTheMarket, Airbnb, and Booking.com.</li>
                <li><strong>&ldquo;User&rdquo;</strong> means any individual who accesses or uses the Service, whether as an account owner, administrator, or team member.</li>
              </ul>
            </section>

            {/* 3 */}
            <section>
              <h2>3. Eligibility and Registration</h2>
              <h3>3.1 Eligibility</h3>
              <p>
                You must be at least 18 years of age and capable of entering into a legally binding contract under the laws of England and Wales. The Service is intended for business use by estate agents, letting agents, property managers, and holiday let owners. It is not intended for consumer use.
              </p>
              <h3>3.2 Account registration</h3>
              <p>
                To use the Service, you must create an Account by providing accurate, current, and complete information. You are responsible for maintaining the confidentiality of your login credentials and for all activity that occurs under your Account. You must notify us immediately at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> if you become aware of any unauthorised access to your Account.
              </p>
              <h3>3.3 One account per user</h3>
              <p>
                Each User may be associated with one Account only. Additional team members may be invited to an Account by the account owner or administrator.
              </p>
              <h3>3.4 Account accuracy</h3>
              <p>
                You agree to keep all Account information accurate and up to date. We reserve the right to suspend or terminate Accounts that contain materially inaccurate or fraudulent information.
              </p>
            </section>

            {/* 4 */}
            <section>
              <h2>4. The Service</h2>
              <h3>4.1 Description</h3>
              <p>
                {COMPANY_NAME} provides a platform that enables Users to import property listings, generate video advertisements using automated rendering, and publish those videos to Third-Party Platforms. The Service includes listing data extraction, automated script generation, voiceover generation, programmatic video rendering, and social media publishing tools.
              </p>
              <h3>4.2 Availability</h3>
              <p>
                We aim to provide continuous access to the Service but do not guarantee uninterrupted or error-free operation. The Service may be temporarily unavailable for maintenance, updates, or due to circumstances beyond our reasonable control. We shall not be liable for any loss arising from Service unavailability.
              </p>
              <h3>4.3 Modifications</h3>
              <p>
                We reserve the right to modify, suspend, or discontinue any part of the Service at any time, with or without notice. We shall not be liable to you or any third party for any modification, suspension, or discontinuance of the Service.
              </p>
            </section>

            {/* 5 */}
            <section>
              <h2>5. Subscriptions and Payment</h2>
              <h3>5.1 Plans and pricing</h3>
              <p>
                The Service is offered under various subscription plans, including a free tier with limited functionality. Details of available plans and pricing are available on request and may be updated from time to time.
              </p>
              <h3>5.2 Billing</h3>
              <p>
                Paid subscriptions are billed in advance on a recurring basis (monthly or annually, depending on your selected plan). Payment is processed via our third-party payment processor, Stripe. You authorise us to charge your designated payment method for all fees due under your Subscription.
              </p>
              <h3>5.3 Automatic renewal</h3>
              <p>
                Subscriptions renew automatically at the end of each billing period unless cancelled before the renewal date. You may cancel at any time through your Account settings. Cancellation takes effect at the end of the current billing period; no refund will be issued for the remaining portion of the period.
              </p>
              <h3>5.4 Price changes</h3>
              <p>
                We may change subscription pricing at any time. Price changes for existing subscribers will take effect at the start of the next billing period following not less than 30 days&rsquo; written notice. Continued use of the Service after a price change constitutes acceptance of the new pricing.
              </p>
              <h3>5.5 Refunds</h3>
              <p>
                All fees are non-refundable except where required by applicable law. If you believe you have been charged in error, contact us at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> within 14 days of the charge.
              </p>
              <h3>5.6 Taxes</h3>
              <p>
                All prices are exclusive of applicable taxes (including VAT) unless otherwise stated. You are responsible for all applicable taxes associated with your Subscription. We will add VAT to invoices where legally required.
              </p>
            </section>

            {/* 6 */}
            <section>
              <h2>6. User Content and Listing Data</h2>
              <h3>6.1 Your content</h3>
              <p>
                You retain all ownership rights in the Content you upload to the Service, including Listing Data, logos, brand assets, and any text you provide or edit. We do not claim ownership of your Content.
              </p>
              <h3>6.2 Licence grant</h3>
              <p>
                By uploading Content to the Service, you grant {COMPANY_NAME} a worldwide, non-exclusive, royalty-free, sublicensable licence to use, reproduce, modify, process, and display your Content solely for the purpose of operating, providing, and improving the Service. This licence terminates when you delete your Content or close your Account, except where your Content has been shared with third parties (e.g., published to social media platforms) prior to deletion.
              </p>
              <h3>6.3 Listing Data from third parties</h3>
              <p>
                When you import a Listing from a Third-Party Platform, you represent and warrant that:
              </p>
              <ol>
                <li>You have the right to use the property listing data and photographs for the purpose of creating marketing materials;</li>
                <li>You are the listing agent, property owner, or have been authorised by the listing agent or property owner to create marketing materials for the property;</li>
                <li>Your use of the imported data complies with the terms of service of the originating Third-Party Platform; and</li>
                <li>No imported Content infringes the intellectual property rights, privacy rights, or any other rights of any third party.</li>
              </ol>
              <h3>6.4 Responsibility for content</h3>
              <p>
                You are solely responsible for all Content uploaded to or created through the Service, including the accuracy of Listing Data, the legality of property descriptions, compliance with advertising standards (including the Property Misdescriptions Act 1991 and Consumer Protection from Unfair Trading Regulations 2008), and the suitability of any Content published to Third-Party Platforms.
              </p>
            </section>

            {/* 7 */}
            <section>
              <h2>7. Generated Content</h2>
              <h3>7.1 Automated output</h3>
              <p>
                The Service uses automated systems, including text-to-speech technology, to generate scripts, captions, voiceover audio, and other content. Generated Content is produced algorithmically and may contain errors, inaccuracies, or inappropriate output.
              </p>
              <h3>7.2 Review obligation</h3>
              <p>
                <strong>You are solely responsible for reviewing, verifying, and approving all Generated Content before use or publication.</strong> We provide automated tools as a starting point; you must ensure that all Generated Content is factually accurate, does not mislead consumers, complies with applicable advertising regulations, and is suitable for publication. {COMPANY_NAME} accepts no liability for claims arising from Generated Content that you have approved and published.
              </p>
              <h3>7.3 No guarantee of quality</h3>
              <p>
                We do not guarantee that Generated Content will be fit for any particular purpose, free from error, or of any particular quality. Automated outputs vary and may not meet your expectations. You may regenerate, edit, or discard any Generated Content at your discretion.
              </p>
              <h3>7.4 Ownership of generated content</h3>
              <p>
                Subject to applicable law, you own the Generated Content produced through your use of the Service. You grant {COMPANY_NAME} a licence to use anonymised, aggregated data derived from Generated Content for the purpose of improving the Service. We shall not use your specific Generated Content for marketing or promotional purposes without your prior written consent.
              </p>
            </section>

            {/* 8 */}
            <section>
              <h2>8. Video Rendering and Publishing</h2>
              <h3>8.1 Rendering</h3>
              <p>
                Video rendering is performed by third-party infrastructure (AWS Lambda). Render times, quality, and availability may vary and are subject to the performance and availability of third-party services. We do not guarantee specific render times or output quality.
              </p>
              <h3>8.2 Social media publishing</h3>
              <p>
                The Service enables you to publish videos to Third-Party Platforms via their respective APIs. By connecting a Third-Party Platform account and initiating a publication, you authorise {COMPANY_NAME} to upload content and post on your behalf using the access tokens you have granted.
              </p>
              <h3>8.3 Third-party platform terms</h3>
              <p>
                Your use of Third-Party Platforms is governed by their respective terms of service, privacy policies, and community guidelines. You are solely responsible for ensuring that Content published via the Service complies with those terms. {COMPANY_NAME} is not responsible for any action taken by a Third-Party Platform in relation to your content, account, or access.
              </p>
              <h3>8.4 Token revocation</h3>
              <p>
                You may disconnect any Third-Party Platform at any time through your Account settings. Disconnection revokes {COMPANY_NAME}&rsquo;s access to that platform. Content already published to that platform will remain there unless you remove it directly on the platform.
              </p>
            </section>

            {/* 9 */}
            <section>
              <h2>9. Acceptable Use</h2>
              <p>You agree not to use the Service to:</p>
              <ol>
                <li>Upload, generate, or publish Content that is unlawful, defamatory, obscene, threatening, harassing, or otherwise objectionable;</li>
                <li>Create misleading property advertisements, including false descriptions, manipulated photographs, or fabricated property details;</li>
                <li>Impersonate any person or entity, or falsely claim an affiliation with any person or entity;</li>
                <li>Infringe the intellectual property rights, privacy rights, or other rights of any third party;</li>
                <li>Upload or transmit viruses, malware, or other harmful code;</li>
                <li>Attempt to gain unauthorised access to the Service, other Users&rsquo; Accounts, or any systems or networks connected to the Service;</li>
                <li>Use the Service for any purpose that violates applicable law, regulation, or industry code of practice, including but not limited to estate agency regulations, consumer protection laws, and data protection legislation;</li>
                <li>Scrape, crawl, or automated-access the Service or its infrastructure except through the interfaces we provide;</li>
                <li>Resell, sublicence, or redistribute the Service or any part of it without our prior written consent; or</li>
                <li>Use the Service to send unsolicited commercial communications (spam).</li>
              </ol>
              <p>
                We reserve the right to suspend or terminate your Account immediately and without notice if we reasonably believe you are in breach of this section.
              </p>
            </section>

            {/* 10 */}
            <section>
              <h2>10. Intellectual Property</h2>
              <h3>10.1 Our intellectual property</h3>
              <p>
                The Service, including its design, code, functionality, branding, logos, trademarks, documentation, and all underlying technology, is owned by {COMPANY_ENTITY} and is protected by copyright, trademark, and other intellectual property laws. Nothing in these Terms grants you any right, title, or interest in or to our intellectual property except as expressly stated.
              </p>
              <h3>10.2 Feedback</h3>
              <p>
                If you provide suggestions, ideas, or other feedback about the Service (&ldquo;Feedback&rdquo;), you grant us an unrestricted, irrevocable, perpetual, royalty-free licence to use that Feedback for any purpose without compensation or attribution.
              </p>
              <h3>10.3 Branding on videos</h3>
              <p>
                Generated videos may include a {COMPANY_NAME} watermark or branding element. The presence and prominence of such branding may vary by subscription plan. Removal of {COMPANY_NAME} branding may be available on premium plans.
              </p>
            </section>

            {/* 11 */}
            <section>
              <h2>11. Data Protection and Privacy</h2>
              <p>
                We process personal data in accordance with our <a href="/privacy">Privacy Policy</a> and applicable data protection legislation, including the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018. By using the Service, you acknowledge and agree to our data processing practices as described in the Privacy Policy.
              </p>
              <p>
                Where you upload personal data of third parties (for example, property occupant information), you warrant that you have obtained all necessary consents and have a lawful basis for processing that data. You shall indemnify {COMPANY_NAME} against any claims arising from your failure to comply with data protection obligations.
              </p>
            </section>

            {/* 12 */}
            <section>
              <h2>12. Limitation of Liability</h2>
              <h3>12.1 Exclusion of warranties</h3>
              <p>
                <strong>The Service is provided &ldquo;as is&rdquo; and &ldquo;as available&rdquo; without warranty of any kind, whether express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, and non-infringement.</strong> We do not warrant that the Service will be uninterrupted, error-free, secure, or free of viruses or other harmful components.
              </p>
              <h3>12.2 Limitation of liability</h3>
              <p>
                To the maximum extent permitted by applicable law:
              </p>
              <ol>
                <li>{COMPANY_ENTITY} shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, revenue, data, business opportunities, or goodwill, however caused and whether in contract, tort (including negligence), breach of statutory duty, or otherwise;</li>
                <li>Our total aggregate liability to you for all claims arising out of or relating to these Terms or the Service shall not exceed the greater of (a) the total fees paid by you in the 12 months preceding the claim, or (b) £100;</li>
                <li>We shall not be liable for any loss arising from (i) your reliance on Generated Content, (ii) actions taken by Third-Party Platforms, (iii) the accuracy or completeness of Listing Data imported from third-party sources, or (iv) your failure to review and approve Content before publication.</li>
              </ol>
              <h3>12.3 Statutory rights</h3>
              <p>
                Nothing in these Terms excludes or limits our liability for (a) death or personal injury caused by our negligence, (b) fraud or fraudulent misrepresentation, or (c) any other liability that cannot be excluded or limited by applicable law.
              </p>
            </section>

            {/* 13 */}
            <section>
              <h2>13. Indemnification</h2>
              <p>
                You agree to indemnify, defend, and hold harmless {COMPANY_ENTITY}, its officers, directors, employees, and agents from and against any and all claims, damages, losses, liabilities, costs, and expenses (including reasonable legal fees) arising out of or relating to:
              </p>
              <ol>
                <li>Your use of the Service;</li>
                <li>Content you upload, create, approve, or publish through the Service;</li>
                <li>Your breach of these Terms;</li>
                <li>Your violation of any applicable law, regulation, or third-party right;</li>
                <li>Your connection to and use of Third-Party Platforms via the Service; or</li>
                <li>Any claim by a property owner, buyer, tenant, or other third party arising from Content you have published.</li>
              </ol>
            </section>

            {/* 14 */}
            <section>
              <h2>14. Suspension and Termination</h2>
              <h3>14.1 Termination by you</h3>
              <p>
                You may close your Account at any time through your Account settings or by contacting us at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. Upon closure, your Subscription (if any) will be cancelled and no further charges will be made. Active subscription periods will not be refunded.
              </p>
              <h3>14.2 Termination by us</h3>
              <p>
                We may suspend or terminate your Account and access to the Service at any time, with or without cause, with or without notice. Grounds for termination include but are not limited to breach of these Terms, non-payment, suspected fraud, or conduct that we reasonably believe is harmful to other Users, the Service, or third parties.
              </p>
              <h3>14.3 Effect of termination</h3>
              <p>
                Upon termination: (a) your right to access and use the Service ceases immediately; (b) we may delete your Account data, including Content and Generated Content, after a 30-day grace period; (c) Content already published to Third-Party Platforms will remain on those platforms unless you remove it directly; and (d) all provisions of these Terms that by their nature should survive termination shall survive, including Sections 6.2, 7.4, 10, 12, 13, and 17.
              </p>
            </section>

            {/* 15 */}
            <section>
              <h2>15. Changes to These Terms</h2>
              <p>
                We may update these Terms from time to time. If we make material changes, we will notify you by email or by posting a notice within the Service at least 14 days before the changes take effect. Your continued use of the Service after the effective date of any changes constitutes your acceptance of the revised Terms. If you do not agree to the revised Terms, you must stop using the Service and close your Account.
              </p>
            </section>

            {/* 16 */}
            <section>
              <h2>16. Force Majeure</h2>
              <p>
                We shall not be liable for any failure or delay in performing our obligations under these Terms where such failure or delay results from circumstances beyond our reasonable control, including but not limited to natural disasters, pandemics, acts of government, war, terrorism, civil unrest, power failures, internet disruptions, or failures of Third-Party Platforms or infrastructure providers.
              </p>
            </section>

            {/* 17 */}
            <section>
              <h2>17. Governing Law and Jurisdiction</h2>
              <p>
                These Terms and any dispute or claim arising out of or in connection with them (including non-contractual disputes or claims) shall be governed by and construed in accordance with the laws of England and Wales.
              </p>
              <p>
                The courts of England and Wales shall have exclusive jurisdiction to settle any dispute or claim arising out of or in connection with these Terms.
              </p>
            </section>

            {/* 18 */}
            <section>
              <h2>18. General Provisions</h2>
              <h3>18.1 Entire agreement</h3>
              <p>
                These Terms, together with the <a href="/privacy">Privacy Policy</a>, constitute the entire agreement between you and {COMPANY_ENTITY} relating to the Service and supersede all prior agreements, representations, and understandings.
              </p>
              <h3>18.2 Severability</h3>
              <p>
                If any provision of these Terms is found to be invalid, illegal, or unenforceable, the remaining provisions shall remain in full force and effect. The invalid provision shall be modified to the minimum extent necessary to make it valid and enforceable.
              </p>
              <h3>18.3 Waiver</h3>
              <p>
                Our failure to enforce any right or provision of these Terms shall not constitute a waiver of that right or provision. Any waiver must be in writing and signed by an authorised representative of {COMPANY_ENTITY}.
              </p>
              <h3>18.4 Assignment</h3>
              <p>
                You may not assign or transfer these Terms or any rights or obligations hereunder without our prior written consent. We may assign these Terms without restriction.
              </p>
              <h3>18.5 Third-party rights</h3>
              <p>
                These Terms do not confer any rights on any person or party other than you and {COMPANY_ENTITY} pursuant to the Contracts (Rights of Third Parties) Act 1999.
              </p>
              <h3>18.6 Notices</h3>
              <p>
                Notices to you may be sent to the email address associated with your Account. Notices to us should be sent to <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
              </p>
            </section>

            {/* 19 */}
            <section>
              <h2>19. Contact</h2>
              <p>
                If you have any questions about these Terms, please contact us:
              </p>
              <ul>
                <li><strong>Email:</strong> <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></li>
                <li><strong>Company:</strong> {COMPANY_ENTITY}</li>
                <li><strong>Website:</strong> <a href={`https://${WEBSITE}`}>{WEBSITE}</a></li>
              </ul>
            </section>

          </div>
        </div>
      </article>
    </PublicLayout>
  )
}
